import { FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormController, MuiModal, MuiTextField } from "../..";
import {
  ClientSearchSaveFilterFormKey,
  FilterProps,
} from "../../../pages/clientSearch/modelsAndSchemas/Models";
import { MAX_LENGTH_20 } from "../../../helpers/constants";
import { ClientSearchSaveFilterValidationSchema } from "../../../pages/clientSearch/modelsAndSchemas/ValidationSchema";
import { useSearchClientSaveFilterMutation } from "../../../api/services/client/clientApis";
import {
  toggleLoader,
  toggleToast,
} from "../../../redux/slices/appState/appStateSlice";
import { trimValue } from "../../../helpers/utils";
import { SaveSearchFilterParams } from "../../../api/services/client/models";
import { useSiteCore } from "../../../hooks/useSiteCore";
import { filterSiteCoreKeys } from "../siteCore/SiteCoreKeys";
import { useNavigationSiteCore } from "../../../hooks/useNavigationSiteCore";
import { COMPONENT_IDS } from "../../../helpers/constants/componentIds";

interface ClientSearchFilterProps {
  showModal: boolean;
  handleModalClose: () => void;
  pageId: string;
  savedSearchPageName: number;
  filters: FilterProps[];
  listOfFilters: any;
  refetchListOfFilters: () => void;
}

export const SaveFilterModal: FC<ClientSearchFilterProps> = ({
  showModal,
  handleModalClose,
  pageId,
  savedSearchPageName,
  filters,
  listOfFilters,
  refetchListOfFilters,
}) => {
  const {
    mutate: postSaveFilter,
    isPending,
    isSuccess,
    isError,
  } = useSearchClientSaveFilterMutation();

  const dispatch = useDispatch();
  const { commonLabels, fetchLabel, fetchValidationMessage } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const fetchFilterPopupLabel = (key: string) => {
    return fetchLabel(filterSiteCoreKeys.filterPopupKey, key);
  };

  const {
    formState: { isValid },
    control,
    getValues,
    setError,
  } = useForm({
    mode: "all",
    defaultValues: {
      [ClientSearchSaveFilterFormKey.filterName]: "",
    },
    resolver: yupResolver(
      ClientSearchSaveFilterValidationSchema(
        listOfFilters,
        fetchValidationMessage,
        fetchFilterPopupLabel,
      ),
    ),
  });

  useEffect(() => {
    if (isSuccess) {
      dispatch(
        toggleToast({
          open: true,
          message: fetchFilterPopupLabel(filterSiteCoreKeys.filterSavedMessage),
        }),
      );
      refetchListOfFilters();
      handleModalClose();
    }
  }, [isSuccess]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isPending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isPending]);

  useEffect(() => {
    isError &&
      setError(ClientSearchSaveFilterFormKey.filterName, {
        message: commonLabels.someThingWentWrongPleaseTryAgainLater,
      });
  }, [isError]);

  const handleSaveFilterClick = async () => {
    const { filterName } = getValues();
    const apiData: SaveSearchFilterParams = {
      name: trimValue(filterName),
      savedSearchPageName,
      filters,
    };
    postSaveFilter(apiData);
  };

  return (
    <MuiModal
      open={showModal}
      onClose={handleModalClose}
      title={fetchFilterPopupLabel(filterSiteCoreKeys.saveFilterTitle)}
      primaryButtonTitle={commonLabels.save}
      secondaryButtonTitle={commonLabels.cancel}
      onPrimaryButtonClick={handleSaveFilterClick}
      onSecondaryButtonClick={handleModalClose}
      disablePrimaryButton={!isValid}
      id={COMPONENT_IDS.SAVE_FILTER_MODAL}
    >
      <FormController
        control={control}
        name={ClientSearchSaveFilterFormKey.filterName}
      >
        <MuiTextField
          id={`${pageId}-${ClientSearchSaveFilterFormKey.filterName}`}
          data-testid="filter-name-input"
          maxLength={MAX_LENGTH_20}
          placeholder={commonLabels.inputPlaceHolder}
          label={fetchFilterPopupLabel(filterSiteCoreKeys.filterName)}
          requiredARIALabel={commonARIALabel.required}
        />
      </FormController>
    </MuiModal>
  );
};
